import classNames from "classnames";
import { ButtonSecondary } from "components/Atoms/Button";
import Panel from "components/panels/panel";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { type TableColumn } from "components/Tables";
import Table from "components/Tables/Table";
import DocumentLifeCyclePill from "components/Document/DocumentLifeCyclePill";
import moment from "moment";
import React from "react";
import { DocumentTypeWithoutUnknown, getDocumentRoutes, hasDocumentBeenSubmitted, isDiscarded, isDocumentOverdue } from "utilities/documentUtils";
import { DeleteDialog } from "../../../components";
import { NoDataAvailableDiv } from "../../../components/NoDataAvailableDiv";
import { formatShortDate } from "../../../utilities";
import { apiResponse, getLinkUnlinkApi } from "../../../utilities/apiUtils";
import { useCustomQuery } from "../../../utilities/hookutils";
import { useEsignOrLegacy } from "components/ModernOrLegacy/hooks";
import CreateEsignForShow from "components/ModernOrLegacy/AppCreateEsignForShow";

interface Props {
  config: server.dto.LinkedDocumentPanelConfig
  showId: number
  bookkeepingId: number
}

const LinkedDocumentPanel = (props: Props) => {
  const [isUnlinkingDocument, setIsUnlinkingDocument] = React.useState<server.dto.DocumentForList>(undefined);
  const canModify = site.permissions[props.config.documentType].canBatchModify;

  const documentsForShowQuery = useCustomQuery<server.dto.ApiResponseWrapper<server.dto.DocumentForList[]>>({
    queryKey: ["documentsForShow", props.showId, props.config.documentType],
    queryFn: async () => window.CoreApi.Document.ListDocumentsByShow({
      showId: props.showId,
      documentType: props.config.documentType
    })
      .then(r => r.data),
    initialData: apiResponse.success([])
  });

  const unlink = async () => {
    await getLinkUnlinkApi(props.config.documentType).unlink({
      documentId: isUnlinkingDocument.documentId,
      showId: props.showId
    });
    documentsForShowQuery.refetch();
    setIsUnlinkingDocument(undefined);
  };

  const onNewClick = async () => {
    esignOrLegacy.displayModernOrLegacy();
  };

  const { refToScrollTo } = useShortcutPanel({
    type: props.config.documentType as DocumentTypeWithoutUnknown,
    label: props.config.headerLabel,
    totalItems: documentsForShowQuery.data.data.length,
    onAddClicked: canModify ? onNewClick : undefined
  });

  const anyESign = documentsForShowQuery.data.data.some(x => x.isV2) || false;

  const getDiscardedClass = (invoice: server.dto.DocumentForList, otherClasses?: string) => classNames(otherClasses, { s1_gray: invoice != null && isDiscarded(invoice.lifeCycle.value) });

  const columns: Array<TableColumn<server.dto.DocumentForList>> = [
    {
      key: "esignIcon",
      header: "",
      size: "36px",
      hidden: !anyESign,
      render: document => document.isV2 && <i className="icon-rocket" title={RESX.ESign.eSign} />,
      className: getDiscardedClass
    }, {
      key: "date",
      header: "",
      className: getDiscardedClass,
      size: "2fr",
      render: document => <div className="flex-column gap-small">
        {document.detailUrl && <a href={document.detailUrl} className={getDiscardedClass(document)}>{document.documentNumber}</a>}
        {!document.detailUrl && <div className={getDiscardedClass(document)}>{document.documentNumber}</div>}
        <div>
          <DocumentLifeCyclePill value={document.lifeCycle.value} overDue={document.overDue} />
        </div>
      </div>
    }, {
      key: "addressee",
      header: "",
      className: getDiscardedClass,
      render: document => document.addressee.name,
      size: "3fr"
    }, {
      key: "dates",
      header: "",
      size: "2fr",
      className: getDiscardedClass,
      render: document => <>
        {(document.isV1 || hasDocumentBeenSubmitted(document.lifeCycle.value)) &&
          <div>
            {RESX.DateTime.Send}: {formatShortDate(moment.utc(document.sendDate))}
          </div>}
        {(document.isV1 || document.lifeCycle.value === "pending") &&
          <div className={classNames({ s1_red: isDocumentOverdue(document) })}>
            {RESX.DateTime.Due}: {formatShortDate(moment.utc(document.dueDate))}
          </div>}
      </>
    }, {
      key: "total",
      header: "",
      className: document => getDiscardedClass(document, "s1_cell_financialamount"),
      render: document => document.sales != null && document.sales.amount !== 0 && <div>{document.sales.formatted}</div>
    }
  ];

  const esignOrLegacy = useEsignOrLegacy();

  const getDetailRoute = (id: number, isV1: boolean) => {
    const routes = getDocumentRoutes(props.config.documentType, id);

    if (isV1) return routes.detail;
    return routes.esign;
  }

  return <Panel
    panelClassName="s1_related-feature-panel"
    panelRef={refToScrollTo}
    title={props.config.headerLabel}
    isLoading={documentsForShowQuery.isInitialLoading}
    hasError={documentsForShowQuery.isError || documentsForShowQuery.data.hasErrors}
    button={canModify && <ButtonSecondary onClick={onNewClick} label={RESX.Buttons.btnNew} />}
  >
    {documentsForShowQuery.data.data.length === 0 && <NoDataAvailableDiv />}

    {documentsForShowQuery.data.data.length > 0 && <Table
      items={documentsForShowQuery.data.data}
      isLoading={false}
      onEdit={canModify ? document => window.location.href = getDetailRoute(document.documentId, document.isV1) : undefined}
      onDelete={canModify ? document => { setIsUnlinkingDocument(document); } : undefined}
      deleteIcon="linkBreak"
      getKey={document => document.documentId.toString()}
      columns={columns}
      hideHeader
    />}

    <CreateEsignForShow
      showId={props.showId}
      documentType={props.config.documentType as Exclude<server.dto.DocumentTypes, "unknown" | "invoice">}
      modernOrLegacyHook={esignOrLegacy}
    />

    <DeleteDialog
      isOpen={isUnlinkingDocument != null}
      title={RESX.GeneralWarnings.Warning}
      message={RESX.Document.UnlinkWarning}
      onDelete={unlink}
      buttonProps={{
        primary: true,
        color: undefined,
        content: RESX.Buttons.BtnOk
      }}
      handleClose={() => { setIsUnlinkingDocument(undefined); }}
    />

  </Panel>;
};

export default LinkedDocumentPanel;